import {Chip, Typography} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import {useState} from "react";
import {LogiCtrlRule, LogiCtrlRuleAction, LogiCtrlStatus} from "../../../../../api/LogiCtrl";
import {getSubItems, hasSubItems, LogiCtrlSubItems} from "./SubItems";
import {InventoryKind} from "../../../../../api/Logistics";
import {LogiCtrlButtons} from "./Buttons";
import {LogiCtrlError} from "./Error";

const defaultRule: LogiCtrlRule = {
    id: -1,
    project: -1,
    kind: null,
    opening: null,
    subtype: null,
    hardwareGroupItem: null,
    div10Item: null,
    glazingItem: null,
    miscProduct: null,
    inventoryRequest: null,
    action: null,
}

export function Row(props: {
    title: string;
    error: string | null;
    edited: boolean;
    ready: boolean;
    skipRules?: boolean;

    rules: LogiCtrlRule[];
    statuses: LogiCtrlStatus[];

    filter: Partial<LogiCtrlRule>;
    onRuleChange: (old: LogiCtrlRule | null, rule: LogiCtrlRule) => void;
    autoExpand?: boolean;
}) {
    const [expanded, setExpanded] = useState(props.autoExpand || false);
    const kind = props.filter.kind as InventoryKind;
    const showMore = hasSubItems(kind) && !!getSubItems(kind, props.statuses, props.filter);

    const topLevel = props.rules.find(r => {
        for(let k in props.filter) {
            if(r[k] !== props.filter[k]) {
                return false;
            }
        }

        return r.subtype === null && r.inventoryRequest === null && r.hardwareGroupItem === null;
    });

     const childRules = props.rules.filter(r => {
         for(let k in props.filter) {
             if(r[k] !== props.filter[k]) {
                 return false;
             }
         }

         return r !== topLevel;
     })

    const action = topLevel?.action || null;

    const setAction = (value: LogiCtrlRuleAction | null) => {
        if(topLevel) {
            props.onRuleChange(topLevel, {...topLevel, action: value});
            childRules.forEach(r => {
                props.onRuleChange(r, {...r, action: "" as any}); // "" means remove
            })
            return;
        }

        props.onRuleChange(null, {
            ...defaultRule,
            ...props.filter,
            id: -1,
            action: value,
        });
    }

    return (
        <div>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
            }}>
                <Typography variant="body2">{props.title}</Typography>
                <div style={{display: "flex", alignItems: "center", gap: 6}}>
                    {props.skipRules ? (
                        <Chip label="Try Update" size="small" variant="outlined" sx={{color: "text.disabled", borderColor: "text.disabled"}} />
                    ) : (<>
                        {props.error ?
                            <Chip label="Error" size="small" color="error" variant="outlined" /> :
                            props.edited ?
                                <Chip label="Edited" size="small" color="success" variant="outlined" /> :
                                <Chip label="Not Modified" size="small" variant="outlined" />
                        }
                        <LogiCtrlButtons value={action} onChange={setAction} inherited="not-released-replace" />
                    </>)}
                </div>
            </div>
            {props.ready && !!props.error && !expanded && <LogiCtrlError error={props.error} />}
            {props.ready && showMore && (
                <div
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        marginTop: 2,
                        userSelect: "none",
                    }}
                >
                    {expanded ?
                        <ExpandLess sx={{fontSize: 16, color: "text.secondary"}} /> :
                        <ExpandMore sx={{fontSize: 16, color: "text.secondary"}} />
                    }
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: 11}}>
                        {expanded ? "Less" : "More"}
                    </Typography>
                </div>
            )}
            {expanded && (
                <LogiCtrlSubItems
                    kind={kind}
                    filter={props.filter}
                    rules={props.rules}
                    statuses={props.statuses}
                    inherited={action}
                    onRuleChange={props.onRuleChange}
                />
            )}
        </div>
    )
}
